import { List, Map } from 'immutable'
import _get from 'lodash/get'
import { get as getConfig } from 'config'
import { isEntitled } from 'services/subscription'
import {
  USER_ACCOUNT_ACTIVE,
  USER_ACCOUNT_PAUSED,
  USER_ACCOUNT_RECURRING_PLAN,
  USER_ACCOUNT_TRANSACTION_STATUS_DECLINED,
} from 'services/user-account'

const config = getConfig()
const winbackFlowActive = !!_get(config, ['features', 'winbackFlow'])

export const WINBACK_ROW = 'winback-new-since-your-last-visit'
export const WINBACK_BANNER = 'winback-banner'
export const WINBACK_FLAG = 'winback_flow__no_checkout'
export const WINBACK_VARIATION = 'winback_flow'

/**
 * Determines whether to show the winback flow
 * @param {Object} auth - Auth object from Redux state
 * @param {Object} user - User object from Redux state
 * @param {Object} userAccount - UserAccount object from Redux state
 * @returns {boolean} - true if winback banner should be shown
 */
export function shouldShowWinbackFlow (
  auth = Map(),
  user = Map(),
  userAccount = Map(),
) {
  // Check if the feature toggle is active
  if (!winbackFlowActive) {
    return false
  }

  // Skip if user is entitled
  const subscriptions = auth.get('subscriptions', List())
  if (isEntitled(subscriptions)) {
    return false
  }

  // Get account data
  const checkoutComplete = user.getIn(['data', 'checkout_complete'], false)
  const accountStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'status'], null)
  const lastPaymentStatus = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'paymentSource', 'lastTransactionStatus'], null)
  const accountHasActiveSubscription = [
    USER_ACCOUNT_ACTIVE,
    USER_ACCOUNT_PAUSED,
    USER_ACCOUNT_RECURRING_PLAN].includes(accountStatus)
  const paymentDeclined = lastPaymentStatus === USER_ACCOUNT_TRANSACTION_STATUS_DECLINED

  // Early return if account is active but payment failed
  if (accountHasActiveSubscription && paymentDeclined) {
    return false
  }

  // Final condition for showing Winback Flow
  return checkoutComplete && !accountHasActiveSubscription
}

/**
 * Gets the winbackFlow feature flag state
 * @returns {boolean} - true if feature flag is active
 */
export function isWinbackFlowActive () {
  return winbackFlowActive
}

/**
 * Gets the winbackFlow feature flag state
 * @param {Object} optimizelyFeature - The Optimizely feature object
 * @returns {boolean} - true if feature flag is active
 */
export function isWinbackFlagEnabled (optimizelyFeature) {
  const flag = optimizelyFeature.getIn(['flags', WINBACK_FLAG, 'enabled']) || false
  const variation = optimizelyFeature.getIn(['flags', WINBACK_FLAG, 'variation'], '') === WINBACK_VARIATION

  return flag && variation && isWinbackFlowActive()
}
